
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface RoleOption {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useRoleOptions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading, error } = useQuery({
    queryKey: ['role-options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_roles')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as RoleOption[];
    },
  });

  const createRole = useMutation({
    mutationFn: async (name: string) => {
      const maxOrder = Math.max(...roles.map(r => r.display_order), -1);
      const { data, error } = await supabase
        .from('character_roles')
        .insert([{ 
          name, 
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-options'] });
      toast({
        title: "Success",
        description: "Role created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create role",
        variant: "destructive",
      });
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<RoleOption> }) => {
      const { data, error } = await supabase
        .from('character_roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-options'] });
      toast({
        title: "Success",
        description: "Role updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update role",
        variant: "destructive",
      });
    },
  });

  const deleteRole = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_roles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-options'] });
      toast({
        title: "Success",
        description: "Role deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete role",
        variant: "destructive",
      });
    },
  });

  const reorderRoles = useMutation({
    mutationFn: async (reorderedItems: { id: string; display_order: number }[]) => {
      const updates = reorderedItems.map(item => 
        supabase
          .from('character_roles')
          .update({ display_order: item.display_order })
          .eq('id', item.id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-options'] });
      toast({
        title: "Success",
        description: "Role order updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reorder roles",
        variant: "destructive",
      });
    },
  });

  return {
    roles,
    isLoading,
    error,
    createRole,
    updateRole,
    deleteRole,
    reorderRoles,
  };
};
